<template>
	<div class="global-store-debug scrollbar">
		<h1>Global Store Debug</h1>
		<div v-html="jsonHTML"></div>
	</div>
</template>

<!-- This code was based off https://codepen.io/WartClaes/pen/QGGPJL -->
<script lang="ts">
	import { defineComponent, ref, watch, onMounted, nextTick } from 'vue';
	import { globalStore } from 'src/stores/global-store';

	export default defineComponent({
		name: 'CGlobalStoreDebug',
		setup() {
			const jsonHTML = ref('');
			const expandedState = ref<{ [key: string]: boolean }>({});

			const saveExpandedState = () => {
				localStorage.setItem('expandedState', JSON.stringify(expandedState.value));
			};

			const loadExpandedState = () => {
				const savedState = localStorage.getItem('expandedState');
				if (savedState) {
					expandedState.value = JSON.parse(savedState);
				}
			};

			const jsonViewer = (json, expandedState, collapsible = false) => {
				const TEMPLATES = {
					item: '<div class="json__item"><div class="json__key">%KEY%</div><div class="json__value json__value--%TYPE%">%VALUE%</div></div>',
					itemCollapsible: '<label class="json__item json__item--collapsible"><input type="checkbox" class="json__toggle" %CHECKED% data-key="%DATAKEY%"/><div class="json__key">%KEY%</div><div class="json__value json__value--type-%TYPE%">%VALUE%</div>%CHILDREN%</label>'
				};

				const getUniqueKey = (parentKey, key) => parentKey ? `${parentKey}.${key}` : key;

				const createItem = (key, value, type) => {
					let element = TEMPLATES.item.replace('%KEY%', key);
					if (type === 'string') {
						element = element.replace('%VALUE%', '"' + value + '"');
					}
					else {
						element = element.replace('%VALUE%', value);
					}
					element = element.replace('%TYPE%', type);
					return element;
				};

				const createCollapsibleItem = (key, value, type, children, parentKey, isOpen) => {
					const element = TEMPLATES.itemCollapsible.replace('%KEY%', key)
						.replace('%VALUE%', type)
						.replace('%TYPE%', type)
						.replace('%CHILDREN%', children)
						.replace('%CHECKED%', isOpen ? 'checked' : '')
						.replace('%DATAKEY%', getUniqueKey(parentKey, key));
					return element;
				};

				const handleChildren = (key, value, type, parentKey) => {
					let html = '';
					for (const item in value) {
						const _key = item;
						const _val = value[item];
						html += handleItem(_key, _val, getUniqueKey(parentKey, key));
					}
					const isOpen = expandedState[getUniqueKey(parentKey, key)] || false;
					return createCollapsibleItem(key, value, type, html, parentKey, isOpen);
				};

				const handleItem = (key, value, parentKey) => {
					const type = typeof value;
					if (typeof value === 'object') {
						if (!value)
							return createItem(key, value, 'object');

						return handleChildren(key, value, 'object', parentKey);
					}
					return createItem(key, value, type);
				};

				const parseObject = (obj, parentKey = '') => {
					let result = '<div class="json">';
					for (const item in obj) {
						const key = item;
						const value = obj[item];
						result += handleItem(key, value, parentKey);
					}
					result += '</div>';
					return result;
				};

				return parseObject(json);
			};

			const applyExpandedState = () => {
				const checkboxes = document.querySelectorAll('.json__toggle');
				checkboxes.forEach((checkbox: HTMLInputElement) => {
					const key = checkbox.getAttribute('data-key');
					if (key && expandedState.value[key]) {
						checkbox.checked = true;
					}
				});
			};

			const addToggleListeners = () => {
				const checkboxes = document.querySelectorAll('.json__toggle');
				checkboxes.forEach((checkbox: HTMLInputElement) => {
					checkbox.addEventListener('change', (event: Event) => {
						const target = event.target as HTMLInputElement;
						const key = target.getAttribute('data-key');
						if (key) {
							expandedState.value[key] = target.checked;
							saveExpandedState();
						}
					});
				});
			};

			const generate = () => {
				jsonHTML.value = jsonViewer(JSON.parse(JSON.stringify(globalStore)), expandedState.value, true);
				nextTick(() => {
					applyExpandedState();
					addToggleListeners();
				});
			};

			watch(globalStore, generate, { deep: true, immediate: true });

			onMounted(() => {
				loadExpandedState();
				generate();
			});

			return {
				jsonHTML
			};
		}
	});
</script>

<style lang="scss">
.global-store-debug {

	svg {
		width: 32px;
	}

	.json {
		font-family: 'Source Code Pro', monospace;
		font-size: 16px;

		& > {

			.json__item {
				display: block;
			}
		}
	}

	.json__item {
		display: none;
		margin-top: 10px;
		padding-left: 20px;
		user-select: none;
	}

	.json__item--collapsible {
		position: relative;
		overflow: hidden;
		cursor: pointer;

		&::before {
			position: absolute;
			left: 5px;
			content: '+';
		}

		&::after {
			position: absolute;
			left: 9px;
			top: 26px;
			width: 1px;
			height: 100%;
			background-color: lightgrey;
			content: '';
		}

		&:hover {

			& > .json__key,
			& > .json__value {
				text-decoration: underline;
			}
		}
	}

	.json__toggle {
		display: none;

		&:checked ~ .json__item {
			display: block;
		}
	}

	.json__key {
		display: inline;
		color: darkblue;

		&::after {
			content: ': ';
		}
	}

	.json__value {
		display: inline;
	}

	.json__value--string {
		color: green;
	}

	.json__value--number {
		color: blue;
	}

	.json__value--boolean {
		color: red;
	}
}
</style>